import React, { useCallback, useRef, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

interface ConfirmOptions {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  variant?: 'auto' | 'dark';
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
}

/**
 * Promise-basierte Bestätigung über den zentralen ConfirmDialog — Ersatz für
 * window.confirm. Nutzung:
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   ...
 *   if (!(await confirm({ title: 'Löschen?', message: '…', danger: true }))) return;
 *   ...
 *   return (<>{confirmDialog}{restlicheUI}</>);
 *
 * Der zentrierte, moderne Dialog ersetzt den nativen Browser-Alert 1:1 —
 * gleicher Kontrollfluss (Promise<boolean>), nur schöner.
 */
export function useConfirm() {
  const [state, setState] = useState<ConfirmState>({ open: false, title: '', message: '' });
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    setState({ ...options, open: true });
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, open: false }));
    resolver.current?.(value);
    resolver.current = null;
  }, []);

  const confirmDialog = (
    <ConfirmDialog
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      danger={state.danger}
      variant={state.variant}
      onConfirm={() => settle(true)}
      onClose={() => settle(false)}
    />
  );

  return { confirm, confirmDialog };
}

export default useConfirm;
